import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink, RefreshCw } from 'lucide-react'
import { api } from '@/api/client'
import { fmtCount, fmtRate, useCreatorMutation, useCreatorPerson } from '@/lib/creators'
import { formatShort } from '@/lib/time'
import {
  Button,
  ConfirmDialog,
  Drawer,
  ErrorState,
  Input,
  KeyValue,
  PlatformMark,
  Skeleton,
  StatusPill,
  errorText,
  useToast,
  type KeyValueItem,
} from '@/ui'
import { Section } from '../campaigns/shared'

/** A person, opened from a row or card of a pool: the profile, the evidence,
 *  the other pools they are in and what can be done about them. */
export function PersonDrawer({ id, poolId, onClose }: { id: number; poolId: number; onClose: () => void }) {
  const q = useCreatorPerson(id)
  const toast = useToast()
  const d = q.data
  const c = d?.creator
  const here = d?.pools.find((p) => p.id === poolId)
  const others = (d?.pools ?? []).filter((p) => p.id !== poolId)
  const [confirming, setConfirming] = useState(false)
  const [reason, setReason] = useState('')

  const enrich = useCreatorMutation(() => api.enrichCreatorPerson(id))
  const remove = useCreatorMutation(() => api.removePoolMember(poolId, id, '직접 뺌'))
  const restore = useCreatorMutation(() => api.restorePoolMember(poolId, id))
  const block = useCreatorMutation(() => api.creatorDoNotContact(id, reason.trim() || '직접 지정'))
  const err = enrich.error ?? remove.error ?? restore.error

  const run = async (fn: () => Promise<unknown>, done: string) => {
    try {
      await fn()
      toast.success(done)
    } catch {
      /* the message shows in the drawer */
    }
  }

  const facts: KeyValueItem[] = c
    ? [
        { label: '팔로워', value: fmtCount(c.followers) },
        ...(c.media_count > 0 ? [{ label: '게시물', value: fmtCount(c.media_count) }] : []),
        ...(c.engagement_rate > 0
          ? [
              { label: '참여율', value: fmtRate(c.engagement_rate) },
              { label: '평균 반응', value: `좋아요 ${Math.round(c.avg_likes)} · 댓글 ${Math.round(c.avg_comments)}` },
            ]
          : []),
        ...(c.category ? [{ label: '분류', value: c.category }] : []),
        ...(c.website ? [{ label: '웹사이트', value: c.website, mono: true }] : []),
        { label: '정보 확인', value: c.looked_up_at ? formatShort(c.looked_up_at) : '아직 안 불러왔다' },
        { label: '마지막 연락', value: d?.last_contacted_at ? formatShort(d.last_contacted_at) : '없음' },
      ]
    : []

  return (
    <Drawer
      open
      onClose={onClose}
      title={c ? `@${c.handle}` : '불러오는 중'}
      meta={
        c && (
          <>
            <PlatformMark platform={c.platform} withName />
            {d?.suppressed ? <StatusPill domain="creator" status="do_not_contact" /> : here && <StatusPill domain="poolMember" status={here.status} />}
            {c.display_name && <span>{c.display_name}</span>}
          </>
        )
      }
      footer={
        c && (
          <>
            <Button icon={RefreshCw} loading={enrich.isPending} onClick={() => void run(() => enrich.mutateAsync(), '다시 조회했다')}>
              다시 조회
            </Button>
            {here?.status === 'removed' ? (
              <Button loading={restore.isPending} onClick={() => void run(() => restore.mutateAsync(), '풀에 되돌렸다')}>
                되돌리기
              </Button>
            ) : (
              here && (
                <Button loading={remove.isPending} onClick={() => void run(() => remove.mutateAsync(), '풀에서 뺐다')}>
                  풀에서 빼기
                </Button>
              )
            )}
            {!d?.suppressed && (
              <Button variant="dangerOutline" onClick={() => setConfirming(true)}>
                연락 금지
              </Button>
            )}
          </>
        )
      }
    >
      {q.error && !d ? (
        <ErrorState title="불러오지 못했다" error={q.error} onRetry={() => q.refetch()} />
      ) : !d || !c ? (
        <Skeleton className="h-64" />
      ) : (
        <div className="space-y-5">
          {d.suppressed && (
            <p role="status" className="text-table text-status-failed">
              연락 금지 명단에 있다. 어느 풀에서도 연락 대상이 되지 않는다.
            </p>
          )}
          <Section
            title="프로필"
            actions={
              c.profile_url ? (
                <a
                  href={c.profile_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-meta font-medium text-accent hover:underline"
                >
                  프로필 열기
                  <ExternalLink size={12} aria-hidden />
                </a>
              ) : undefined
            }
          >
            <KeyValue items={facts} />
            {c.bio && <p className="whitespace-pre-wrap break-words rounded-md bg-muted/60 p-3 text-table">{c.bio}</p>}
            {c.lookup_error && (
              <p role="alert" className="text-table text-status-failed">
                {c.lookup_error}
              </p>
            )}
          </Section>

          {(c.posts ?? []).length > 0 && (
            <Section title="근거가 된 글" aside="최근 글 최대 3개">
              <ul className="divide-y divide-border">
                {(c.posts ?? []).map((post, i) => (
                  <li key={i} className="space-y-1 py-2 text-table first:pt-0 last:pb-0">
                    <p className="break-words">{post.text.length > 200 ? `${post.text.slice(0, 200)}…` : post.text || '(글 없음)'}</p>
                    <div className="flex items-center gap-3 text-meta text-muted-foreground">
                      {post.posted_at && <span>{formatShort(post.posted_at)}</span>}
                      {post.url && (
                        <a href={post.url} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                          글 열기
                        </a>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <Section title="다른 풀">
            {others.length === 0 ? (
              <p className="text-table text-muted-foreground">다른 풀에는 없다.</p>
            ) : (
              <ul className="space-y-1.5 text-table">
                {others.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-2">
                    <Link to={`/creators/${p.id}`} className="min-w-0 truncate text-accent hover:underline">
                      {p.name}
                    </Link>
                    <StatusPill domain="poolMember" status={p.status} />
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {err != null && (
            <p role="alert" className="text-table text-status-failed">
              {errorText(err)}
            </p>
          )}
        </div>
      )}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="연락 금지로 옮길까"
        description="이 사람은 모든 풀에서 빠지고, 앞으로 어떤 풀에서도 연락 대상이 되지 않는다."
        body={<Input aria-label="이유" placeholder="이유 (선택)" value={reason} onChange={(e) => setReason(e.target.value)} />}
        confirmLabel="연락 금지"
        confirmVariant="destructive"
        loading={block.isPending}
        onConfirm={async () => {
          try {
            await block.mutateAsync()
            toast.success('연락 금지로 옮겼다')
            setConfirming(false)
          } catch (e) {
            toast.error(`옮기지 못했다: ${errorText(e)}`)
          }
        }}
      />
    </Drawer>
  )
}
